import Guid from "common/values/guid/guid";
import UserProfile, { UserProfileJSON } from "users/values/user-profile/user-profile";

export interface NetworkInvitationInfoJSON {
  id: string;
  status: string;
  inviter: boolean;
  individualId?: string;
  avatarId?: string;
  user: UserProfileJSON;
  created: string;
  message?: string;
}

export default class NetworkInvitationInfo {
  id: Guid;
  status: string;
  inviter: boolean;
  individualId: Guid | undefined;
  avatarId?: Guid;
  user: UserProfile;
  created: Date | string;
  message: string;

  constructor(
    id: Guid,
    status: string,
    inviter: boolean,
    individualId: Guid | undefined,
    avatarId: Guid | undefined,
    user: UserProfile,
    created: Date | string,
    message: string
  ) {
    this.id = id;
    this.status = status.toLowerCase();
    this.inviter = inviter;
    this.individualId = individualId;
    this.avatarId = avatarId;
    this.user = user;
    this.created = created;
    this.message = message ?? "";
  }

  public static fromJSON(json: NetworkInvitationInfoJSON): NetworkInvitationInfo {
    if (!json.id) {
      throw new Error("NetworkInvitationInfo must have an id");
    }
    if (!json.status) {
      throw new Error("NetworkInvitationInfo must have a status");
    }
    if (json.inviter === undefined) {
      throw new Error("NetworkInvitationInfo must indicate whether you are the inviter");
    }
    if (!json.user) {
      throw new Error("NetworkInvitationInfo must have a user");
    }
    if (!json.created) {
      throw new Error("NetworkInvitationInfo must have a created");
    }

    const id = Guid.fromJSON(json.id);
    if (!id) throw new Error("NetworkInvitationInfo must have an id");
    const status = json.status;
    const inviter = json.inviter;
    let individualId: Guid | undefined;
    try {
      individualId = json.individualId ? Guid.fromJSON(json.individualId) : undefined;
    } catch {
      console.warn("NetworkInvitationInfo individualId is not a valid Guid");
    }

    const avatarId = json.avatarId ? Guid.fromJSON(json.avatarId) : undefined;
    const user = UserProfile.fromJSON(json.user);
    const created = json.created;
    const message = json.message ?? "";

    return new NetworkInvitationInfo(
      id,
      status,
      inviter,
      individualId,
      avatarId,
      user,
      created,
      message
    );
  }
  public toJSON(): NetworkInvitationInfoJSON {
    return {
      id: this.id.toJSON(),
      status: this.status,
      inviter: this.inviter,
      individualId: this.individualId?.toJSON(),
      avatarId: this.avatarId?.toJSON(),
      user: this.user.toJSON(),
      created: typeof this.created === "string" ? this.created : this.created.toJSON(),
      message: this.message,
    }
  }
}
